import { useCallback, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';

import type { ChatMessageContent, ChatMessageStatus } from '@karehero/models';

import { useDispatch } from 'store';
import { newToast } from 'store/slices/uiSlice';

/** Sends a message's payload, resolving with any content learned from it. */
export type MessageRequest = () => Promise<
  Partial<ChatMessageContent> | undefined
>;

/** What a message is, for a sender that decides how it goes: text waits, anything else does not. */
export interface SentMessage {
  kind: 'text' | 'voice' | 'file';
  /** What it says, where that is known. */
  words?: string;
}

/** Records a message's send status, and any content learned from sending it. */
export type UpdateMessage = (
  messageId: string,
  update: { status: ChatMessageStatus; content?: Partial<ChatMessageContent> },
) => void;

/**
 * useMessageOutbox sends thread messages, marking each sending, then sent or
 * error, and counts what is in flight and what has landed. A failed request is
 * kept by message id so it can be resent.
 */
export const useMessageOutbox = (updateMessage: UpdateMessage) => {
  const dispatch = useDispatch();
  // Files cannot live in the store, so failed requests are kept here. Each
  // stays only while its message shows as failed, so it can be resent.
  const failedRequests = useRef(new Map<string, MessageRequest>());
  const [pendingCount, setPendingCount] = useState(0);
  const [sentCount, setSentCount] = useState(0);

  const send = useCallback(
    (messageId: string, request: MessageRequest | null) => {
      // Only kept, with nothing to send.
      if (!request) return;
      failedRequests.current.delete(messageId);
      updateMessage(messageId, { status: 'sending' });
      setPendingCount((count) => count + 1);
      request()
        .then((content) => {
          setSentCount((count) => count + 1);
          updateMessage(messageId, { status: 'sent', content });
        })
        .catch((err) => {
          Sentry.captureException(err);
          failedRequests.current.set(messageId, request);
          updateMessage(messageId, { status: 'error' });
        })
        .finally(() => setPendingCount((count) => count - 1));
    },
    [updateMessage],
  );

  const resend = useCallback(
    (messageId: string) => {
      const request = failedRequests.current.get(messageId);
      if (request) {
        send(messageId, request);
        return;
      }
      // The request went with the page it was made on; the message did not.
      dispatch(
        newToast({
          title: "That didn't send",
          description: 'We no longer have it to resend. Please send it again.',
          variant: 'error',
        }),
      );
    },
    [dispatch, send],
  );

  return { send, resend, pendingCount, sentCount };
};
